import {
    Component, computed, ElementRef,
    EventEmitter,
    HostListener,
    inject,
    input,
    OnInit,
    Output, signal, ViewChild
} from '@angular/core';
import {animate, style, transition, trigger} from '@angular/animations';
import {PureOriginalService} from "../../../service/pure-original.service";
import {CarImageService} from "../../../service/CarImage.service";
import {PureOriginalsDetailsModel} from "../../../models/PureOriginalsDetails-model";
import {PureOriginalUserModel} from "../../../models/PureOriginalUser-model";
import {UpdatePureOriginalModel} from "../../../models/UpdatePureOriginal-model";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {NotificationService} from "../../../service/Notification.service";
import {AuthService} from "../../../service/auth.service";
import {MatTooltip} from "@angular/material/tooltip";


@Component({
    selector: 'app-pure-originals-info',
    templateUrl: './pure-originals-info.component.html',
    styleUrls: ['./pure-originals-info.component.css'],
    animations: [
        trigger('expandCollapse', [
            transition(':enter', [
                style({height: 0, opacity: 0, transform: 'translateY(-4px)'}),
                animate('220ms ease', style({height: '*', opacity: 1, transform: 'translateY(0)'}))
            ]),
            transition(':leave', [
                style({height: '*', opacity: 1, transform: 'translateY(0)'}),
                animate('180ms ease', style({height: 0, opacity: 0, transform: 'translateY(-4px)'}))
            ])
        ])
    ]
})
export class PureOriginalsInfoComponent implements OnInit {

    public pureOriginalService = inject(PureOriginalService);
    public carImageService = inject(CarImageService);
    public elRef = inject(ElementRef);
    public notificationService = inject(NotificationService);
    private authService = inject(AuthService);

    @Output() public isFileModalOpened: EventEmitter<boolean> = new EventEmitter<boolean>();
    @ViewChild('historyTooltip') historyTooltip: MatTooltip;

    pureOriginalId = input<number>();
    buttonIsDisabled = input<boolean>();
    public isInputChanged = signal<boolean>(false);
    public isVisibleButton = signal<boolean>(false);
    public selectedTab = this.pureOriginalService.selectedTab;
    showMore = false;

    private previousFormValues: any;
    error = input<string>();
    @Output() pureOriginalUser = new EventEmitter<PureOriginalUserModel>
    public tooltipMessage = signal<string>("");

    public pureOriginalDetailObject = computed(() => {
        return this.pureOriginalService.pureOriginalDetails();
    })

    // Computed counts for tabs
    getCompletedCount(): number {
        return this.pureOriginalService.getSuccessfullyTunedMatchingDetails().length;
    }

    getPartiallyTunedCount(): number {
        return this.pureOriginalService.getPartiallyTunedMatchingDetails().length;
    }

    getMissingCount(): number {
        return this.pureOriginalService.pureOriginalMatchingDetails()?.pureOriginalMissingVersions?.length || 0;
    }

    getImpossibleCount(): number {
        return this.pureOriginalService.pureOriginalMatchingDetails()?.pureOriginalImpossibleTunings?.length || 0;
    }

    selectTab(tab: 'completed' | 'partial' | 'missing' | 'impossible') {
        this.selectedTab.set(tab);
    }

    downloadBaseFile() {
        this.downloadVersionFile(this.pureOriginalId(), this.pureOriginalDetailObject().winOlsReference, 'PURE_ORIGINAL_BASE');
    }

    downloadPartialTuningsAction() {
        this.downloadVersionFile(this.pureOriginalId(), this.pureOriginalDetailObject().winOlsReference, 'PURE_ORIGINAL_LOSE_MATCHING');
    }

    addTuning() {
        this.openFileUploadModal();
    }

    uploadIniAction() {
        // Trigger the file input from HTML
        const fileInput = this.elRef.nativeElement.querySelector('#fileInput');
        if (fileInput) {
            fileInput.click();
        }
    }

    editTuning() {
        this.editPureOriginalItem(this.pureOriginalDetailObject().id, this.pureOriginalDetailObject());
    }

    deleteAction() {
        this.deletePureOriginalItem(+this.pureOriginalDetailObject().id, 'PURE_ORIGINAL_BASE', true);
    }

    restartProcess() {
        this.repeatPureOriginalsProcess(+this.pureOriginalDetailObject()['id']);
    }

    pureOriginalDetailsFormGroup = new FormGroup({
        vehicleProducer: new FormControl(''),
        vehicleSeries: new FormControl(''),
        vehicleModel: new FormControl(''),
        vehicleModelYear: new FormControl(''),
        vehicleBuild: new FormControl(''),
        vehicleCharacteristic: new FormControl(''),
        ecuSoftwareVersion: new FormControl(''),
        ecuUse: new FormControl(''),
        ecuBuild: new FormControl(''),
        ecuProducer: new FormControl(''),
        ecuProdNr: new FormControl(''),
        ecuStgNr: new FormControl(''),
        engineDisplacement: new FormControl(0, Validators.pattern(/^\d+(\.\d+)?$/)),
        engineMaxTorque: new FormControl(0, Validators.pattern(/^\d+(\.\d+)?$/)),
        engineName: new FormControl(''),
        engineProducer: new FormControl(''),
        engineType: new FormControl(''),
        engineTransmission: new FormControl(''),
        outputPs: new FormControl(0, Validators.pattern(/^\d+(\.\d+)?$/)),
        outputKw: new FormControl(0, Validators.pattern(/^\d+(\.\d+)?$/)),
        readingHardware: new FormControl(''),
        vehicleType: new FormControl(''),
        ecuSoftwareVersionVersion: new FormControl(''),
        engineEmission: new FormControl(''),
        projectComment: new FormControl(''),
        versionComment: new FormControl(''),
        projectType: new FormControl('')
    });


    public editFlags = {
        id: false,
        vehicleProducer: false,
        vehicleSeries: false,
        vehicleModel: false,
        vehicleModelYear: false,
        vehicleBuild: false,
        ecuBuild: false,
        ecuProducer: false,
        ecuUse: false,
        engineDisplacement: false,
        engineMaxTorque: false,
        engineProducer: false,
        engineType: false,
        engineTransmission: false,
        outputPs: false,
        outputKw: false,
        vehicleType: false,
        ecuSoftwareVersion: false,
        ecuSoftwareVersionVersion: false,
        vehicleCharacteristic: false,
        ecuStgNr: false,
        readingHardware: false,
        ecuProdNr: false,
        dateUpdated: false,
        dateExported: false,
        dateUploaded: false


    };

    ngOnInit() {
        this.restartPureOriginalInfoDetails();
        this.initializeValueChangeSubscriptions();
        this.getPureOriginalsDetails();
    }

    restartPureOriginalInfoDetails() {
        this.pureOriginalService.pureOriginalDetails.set({} as PureOriginalsDetailsModel);
        this.carImageService.image = null
    }

    toggleShowMore() {
        this.showMore = !this.showMore;
    }

    editFormValue(pureOriginalDetails: any) {
        this.pureOriginalDetailsFormGroup.setValue({
            vehicleProducer: pureOriginalDetails.vehicleProducer || '',
            vehicleSeries: pureOriginalDetails.vehicleSeries || '',
            vehicleModel: pureOriginalDetails.vehicleModel || '',
            vehicleModelYear: pureOriginalDetails.vehicleModelYear || '',
            vehicleBuild: pureOriginalDetails.vehicleBuild || '',
            vehicleCharacteristic: pureOriginalDetails.vehicleCharacteristic || '',
            ecuSoftwareVersion: pureOriginalDetails.ecuSoftwareVersion || '',
            ecuUse: pureOriginalDetails.ecuUse || '',
            ecuBuild: pureOriginalDetails.ecuBuild || '',
            ecuProducer: pureOriginalDetails.ecuProducer || '',
            ecuProdNr: pureOriginalDetails.ecuProdNr || '',
            ecuStgNr: pureOriginalDetails.ecuStgNr || '',
            engineDisplacement: pureOriginalDetails.engineDisplacement || 0,
            engineMaxTorque: pureOriginalDetails.engineMaxTorque || 0,
            engineName: pureOriginalDetails.engineName || '',
            engineProducer: pureOriginalDetails.engineProducer || '',
            engineType: pureOriginalDetails.engineType || '',
            engineTransmission: pureOriginalDetails.engineTransmission || '',
            outputPs: pureOriginalDetails.outputPs || 0,
            outputKw: pureOriginalDetails.outputKw || 0,
            readingHardware: pureOriginalDetails.readingHardware || '',
            vehicleType: pureOriginalDetails.vehicleType || '',
            ecuSoftwareVersionVersion: pureOriginalDetails.ecuSoftwareVersionVersion || '',
            engineEmission: pureOriginalDetails.engineEmission || '',
            projectComment: pureOriginalDetails.projectComment || '',
            versionComment: pureOriginalDetails.versionComment || '',
            projectType: pureOriginalDetails.projectType || ''
        });
        this.previousFormValues = this.pureOriginalDetailsFormGroup.value;
    }


    initializeValueChangeSubscriptions() {
        Object.keys(this.pureOriginalDetailsFormGroup.controls).forEach(key => {
            const control = this.pureOriginalDetailsFormGroup.get(key);
            control.valueChanges.subscribe(() => {
                this.isInputChanged.set(true);
            });
        });
    }

    getPureOriginalsDetails() {
        this.pureOriginalService.getPureOriginalDetails(this.pureOriginalId()).then((response) => {
            this.editFormValue(response as PureOriginalsDetailsModel);
            if (response.pureOriginalUserRecords !== null) {
                this.pureOriginalUser.emit(response.pureOriginalUserRecords)
            }
        }, error => {
            if (error.status === 404) {
                this.pureOriginalService.errorMessage.set('File not found!');
            }
        })
    }

    openFileUploadModal() {
        this.isFileModalOpened.emit(true);
    }

    downloadVersionFile(id: number, winOls: string, tableName: string) {
        this.pureOriginalService.downloadFileFromDatabase(id, winOls, tableName)
    }

    isDownloadInProgress(id: number) {
        return this.pureOriginalService.isDownloadInProgress(id);
    }

    deletePureOriginalItem(id: number, tableName: string, pureOriginalDetailsOpened: boolean) {
        this.pureOriginalService.deletePureOriginalItem(id, tableName, pureOriginalDetailsOpened);
    }

    repeatPureOriginalsProcess(id: number) {
        this.pureOriginalService.repeatPureOriginalsProcess(id);
    }

    async editPureOriginalItem(id: string, editItem: PureOriginalsDetailsModel) {
        const response = await this.pureOriginalService.updatePureOriginalDetails(id, editItem);
        this.editFormValue(response);
    }


    async submitForm() {
        if (this.pureOriginalDetailsFormGroup.valid) {
            const formValues = this.pureOriginalDetailsFormGroup.value as UpdatePureOriginalModel;
            const id = this.pureOriginalDetailObject().id;
            try {
                const response = await this.pureOriginalService.editPureOriginalDetailsOnClick(+id, formValues);
                this.editFormValue(response as PureOriginalsDetailsModel);
            } catch (error) {
                this.cancelForm();
            }
            this.closeAllEdits();
        } else {
            return;
        }
    }

    toggleEdit(field: string | null, $event: any) {
        $event.stopPropagation();
        console.log(this.buttonIsDisabled());
        if (this.buttonIsDisabled()) {
            return;
        }
        if (field === null) {
            this.closeAllEdits();
        } else {
            this.isVisibleButton.set(true);
            this.editFlags[field] = !this.editFlags[field];

            if (this.editFlags[field]) {
                const updatedValue = this.pureOriginalDetailObject()?.[field];
                if (updatedValue !== undefined) {
                    this.pureOriginalDetailsFormGroup.get(field)?.setValue(updatedValue);
                }
            }
        }
    }

    stopPropagation(event: Event) {
        event.stopPropagation();
    }

    closeAllEdits() {
        this.isVisibleButton.set(false);
        Object.keys(this.editFlags).forEach(key => {
            this.editFlags[key] = false;
        });
    }

    cancelForm() {
        this.pureOriginalDetailsFormGroup.reset(this.previousFormValues);
        this.closeAllEdits()
    }

    @HostListener('document:click', ['$event'])
    handleClickOutside(event: Event) {
        const element = this.elRef.nativeElement.querySelector('.matching-item-text');
        if (element && !element.contains(event.target as Node)) {
            this.closeAllEdits();
        }
    }

    uploadIni(event: any) {
        const fileInput = event.target as HTMLInputElement;
        let file = fileInput.files?.[0];

        if (!file) return;

        this.pureOriginalService.uploadIniFile(file, this.pureOriginalId())
            .then(response => {
                this.editFormValue(response);
                fileInput.value = '';
            })
            .catch(error => {
                console.error('Error uploading INI file:', error);
                fileInput.value = '';
            });

        file = null;
    }

    openHistoryModal(pureOriginalId: number) {
        this.pureOriginalService.getPureOriginalHistory(pureOriginalId).subscribe((response) => {
            if (response.status === 200) {
                if (response.body.length > 0) {
                    this.pureOriginalService.showPureOriginalHistory(pureOriginalId, response.body);
                } else {
                    this.tooltipMessage.set("No history available for this PureOriginal");

                    setTimeout(() => {
                        this.historyTooltip.show();
                    }, 0);
                }

            }
        });
    }

    isSuperAdmin() {
        return this.authService.isSuperAdmin();
    }
}

export class CustomPureOriginalsUploadFileValidations {
    message: string;
    hasError: boolean;
}


